import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, Loader2, Package2, ToggleLeft, ToggleRight, Wine, Star } from 'lucide-react';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { eur } from '@/lib/format';
import { ImageUploader } from '@/components/ImageUploader';

const EMPTY_FORM = {
  name: '',
  description: '',
  image_url: '',
  price: 12.9,
  kind: 'custom',            // 'custom' (poke da comporre con size) | 'signature' (poke firmata)
  size_id: '',
  signature_bowl_id: '',
  drink_ids: [],
  featured: true,
  active: true,
  sort_order: 0,
};

export default function AdminCombosPage() {
  const [combos, setCombos] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, sig, drk] = await Promise.all([
        api.get('/combos?active_only=false'),
        api.get('/ingredients?category=size'),
        api.get('/signature-bowls'),
        api.get('/ingredients?category=beverage'),
      ]);
      setCombos(c.data);
      setSizes(s.data);
      setSignatures(sig.data);
      setDrinks(drk.data);
    } catch (e) {
      toast.error('Errore caricamento combos');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      size_id: sizes.find(s => s.name.toLowerCase().includes('media'))?.id || sizes[0]?.id || '',
    });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      description: c.description || '',
      image_url: c.image_url || '',
      price: c.price,
      kind: c.kind || (c.signature_bowl_id ? 'signature' : 'custom'),
      size_id: c.size_id || '',
      signature_bowl_id: c.signature_bowl_id || '',
      drink_ids: c.drink_ids || [],
      featured: c.featured !== false,
      active: c.active,
      sort_order: c.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Inserisci il nome');
    if (form.kind === 'custom' && !form.size_id) return toast.error('Seleziona la taglia della poke');
    if (form.kind === 'signature' && !form.signature_bowl_id) return toast.error('Seleziona la poke firmata');
    if (form.drink_ids.length === 0) return toast.error('Seleziona almeno una bevanda');
    if (form.price <= 0) return toast.error('Prezzo non valido');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        image_url: form.image_url,
        price: Number(form.price),
        kind: form.kind,
        size_id: form.kind === 'custom' ? form.size_id : null,
        signature_bowl_id: form.kind === 'signature' ? form.signature_bowl_id : null,
        drink_ids: form.drink_ids,
        featured: form.featured,
        active: form.active,
        sort_order: Number(form.sort_order || 0),
      };
      if (editing) {
        await api.patch(`/admin/combos/${editing.id}`, payload);
        toast.success('Menù aggiornato');
      } else {
        await api.post('/admin/combos', payload);
        toast.success('Menù creato');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const toggleActive = async (c) => {
    try { await api.patch(`/admin/combos/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error('Errore'); }
  };
  const del = async (c) => {
    if (!window.confirm(`Eliminare il menù "${c.name}"?`)) return;
    try { await api.delete(`/admin/combos/${c.id}`); toast.success('Menù eliminato'); load(); }
    catch (e) { toast.error('Errore'); }
  };

  const toggleDrink = (id) => {
    setForm(f => ({
      ...f,
      drink_ids: f.drink_ids.includes(id) ? f.drink_ids.filter(x => x !== id) : [...f.drink_ids, id],
    }));
  };

  return (
    <AdminLayout testid="admin-combos-content">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Menù (Combo)</h1>
          <p className="text-sm text-[--brand-muted] mt-1">Combinazioni poke + bevanda a prezzo fisso</p>
        </div>
        <Button onClick={openCreate} className="rounded-xl bg-[--brand-primary] hover:bg-[--brand-primary-hover]" data-testid="admin-combo-add-button">
          <Plus size={16} className="mr-1"/>Nuovo menù
        </Button>
      </div>

      {loading ? (
        <div className="rounded-2xl bg-white border border-[--brand-border] p-8 text-center text-[--brand-muted]"><Loader2 className="animate-spin inline mr-2" size={16}/> Caricamento...</div>
      ) : combos.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[--brand-border] p-8 text-center">
          <Package2 size={32} className="mx-auto text-[--brand-muted] mb-2"/>
          <div className="font-semibold">Nessun menù creato</div>
          <div className="text-sm text-[--brand-muted] mt-1">Crea il tuo primo menù combo.</div>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {combos.map(c => {
            const size = sizes.find(s => s.id === c.size_id);
            const sig  = signatures.find(s => s.id === c.signature_bowl_id);
            return (
              <div key={c.id} className="rounded-2xl bg-white border border-[--brand-border] overflow-hidden shadow-[var(--shadow-sm)]" data-testid="admin-combo-card">
                {c.image_url && (
                  <div className="aspect-[4/3] bg-[--brand-surface-2]">
                    <img src={c.image_url} alt={c.name} className="w-full h-full object-cover"/>
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold">{c.name}</h3>
                        {c.active ? <Badge className="bg-[--brand-primary]/10 text-[--brand-primary] border-[--brand-primary]/30 text-[10px]">Attivo</Badge>
                                  : <Badge variant="secondary" className="text-[10px]">Disattivo</Badge>}
                        {c.featured && <Badge className="bg-[--brand-accent]/15 text-[--brand-accent] border-[--brand-accent]/30 text-[10px]"><Star size={10} className="inline mr-0.5"/>Home</Badge>}
                      </div>
                      {c.description && <div className="text-xs text-[--brand-muted] mt-1 line-clamp-2">{c.description}</div>}
                    </div>
                    <div className="font-display text-xl font-bold text-[--brand-primary] tabular-nums">{eur(c.price)}</div>
                  </div>
                  <div className="mt-3 text-xs text-[--brand-muted] space-y-0.5">
                    <div>🥗 {c.kind === 'signature' ? (sig ? sig.name : '—') : (size ? `${size.name} (da comporre)` : '—')}</div>
                    <div className="flex items-center gap-1"><Wine size={12}/> {c.drink_ids.length} bevande a scelta</div>
                  </div>
                  <div className="flex gap-1 mt-3 justify-end">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive(c)} title={c.active ? 'Disattiva' : 'Attiva'}>
                      {c.active ? <ToggleRight size={16}/> : <ToggleLeft size={16}/>}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Modifica"><Edit size={14}/></Button>
                    <Button variant="ghost" size="icon" className="text-[--brand-danger]" onClick={() => del(c)}><Trash2 size={14}/></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica menù' : 'Nuovo menù combo'}</DialogTitle>
            <DialogDescription>Configura un'offerta poke + bevanda a prezzo unico.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome menù</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Es. Menù Poke Media + Bevanda" className="rounded-xl" data-testid="combo-form-name"/>
            </div>
            <div>
              <Label>Descrizione (opzionale)</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Es. Poke media da comporre + bevanda inclusa" className="rounded-xl"/>
            </div>
            <ImageUploader value={form.image_url} onChange={(url) => setForm(f => ({ ...f, image_url: url }))} testid="combo-image-uploader"/>

            {/* KIND selector */}
            <div>
              <Label>Tipo di poke inclusa</Label>
              <Tabs value={form.kind} onValueChange={(v) => setForm(f => ({ ...f, kind: v }))}>
                <TabsList className="grid grid-cols-2 w-full bg-[--brand-surface-2] p-1 rounded-xl">
                  <TabsTrigger value="custom" className="rounded-lg data-[state=active]:bg-white">Da comporre (Taglia)</TabsTrigger>
                  <TabsTrigger value="signature" className="rounded-lg data-[state=active]:bg-white">Poke firmata</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {form.kind === 'custom' ? (
              <div>
                <Label>Taglia della poke</Label>
                <Select value={form.size_id} onValueChange={(v) => setForm(f => ({ ...f, size_id: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="combo-form-size"><SelectValue placeholder="Seleziona una taglia..."/></SelectTrigger>
                  <SelectContent>
                    {sizes.filter(s => s.active).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} — {eur(s.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[--brand-muted] mt-1">Il cliente comporrà la sua poke di questa taglia usando gli ingredienti disponibili.</p>
              </div>
            ) : (
              <div>
                <Label>Poke firmata inclusa</Label>
                <Select value={form.signature_bowl_id} onValueChange={(v) => setForm(f => ({ ...f, signature_bowl_id: v }))}>
                  <SelectTrigger className="rounded-xl" data-testid="combo-form-signature"><SelectValue placeholder="Seleziona una poke firmata..."/></SelectTrigger>
                  <SelectContent>
                    {signatures.filter(s => s.active).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} — {eur(s.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Prezzo combo (€)</Label>
                <Input type="number" step="0.10" value={form.price} onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} className="rounded-xl" data-testid="combo-form-price"/>
              </div>
              <div>
                <Label>Ordine</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} className="rounded-xl"/>
              </div>
            </div>

            <div>
              <Label>Bevande a scelta ({form.drink_ids.length} selezionate)</Label>
              {drinks.length === 0 ? (
                <div className="rounded-xl bg-[#FFF6E1] border border-[#F4D89A] px-4 py-3 text-xs mt-1">
                  ⚠️ Nessuna bevanda. Vai in <b>Menu → Bevande</b> e aggiungine qualcuna.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 mt-1 max-h-52 overflow-y-auto p-2 border border-[--brand-border] rounded-xl">
                  {drinks.map(d => {
                    const on = form.drink_ids.includes(d.id);
                    return (
                      <button key={d.id} type="button" onClick={() => toggleDrink(d.id)}
                        className={`text-left rounded-lg border p-2 text-sm transition ${on ? 'border-[--brand-primary] bg-[#F3FBF7]' : 'border-[--brand-border] bg-white hover:bg-[--brand-surface-2]'}`}
                        data-testid={`combo-drink-${d.id}`}>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={on} readOnly className="accent-[--brand-primary]"/>
                          <span className="font-semibold flex-1">{d.name}</span>
                          {d.price > 0 && <span className="text-xs text-[--brand-muted] tabular-nums">{eur(d.price)}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm(f => ({ ...f, featured: v }))}/>
                Mostra in home page (in evidenza)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={form.active} onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))}/>
                Menù attivo
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} disabled={saving} className="rounded-xl bg-[--brand-primary]" data-testid="combo-form-save">
              {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : null}Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
